/**
 * Schemas Zod da criação (Fase 5): biblioteca de criativos, públicos, assistente (rascunho),
 * criação em massa, templates e duplicação entre contas. O rascunho aceita campos incompletos
 * (é salvo a cada etapa); a publicação exige `validateDraftForPublish` sem problemas.
 */
import { z } from "zod";
import { CAMPAIGN_OBJECTIVES } from "./campaign-enums.js";
import {
  AD_FORMATS,
  ASSET_KINDS,
  BID_STRATEGIES,
  BILLING_EVENTS,
  BULK_MAX_ADS,
  CALL_TO_ACTIONS,
  CAROUSEL_MAX_CARDS,
  DESTINATION_TYPES,
  DRAFT_KINDS,
  ENGAGEMENT_SOURCES,
  IMAGE_MIME_TYPES,
  OPTIMIZATION_GOALS,
  PIXEL_EVENTS,
  PREVIEW_FORMATS,
  PUBLISHER_PLATFORMS,
  SPECIAL_AD_CATEGORIES,
  VIDEO_MIME_TYPES,
  WIZARD_STEPS,
} from "./creation-enums.js";

const actIdSchema = z
  .string()
  .trim()
  .regex(/^act_\d{1,30}$/, "Informe uma conta de anúncio válida.");
const metaNumericId = z
  .string()
  .trim()
  .regex(/^\d{1,30}$/, "Id inválido.");
const optionalMetaId = metaNumericId.nullable().default(null);
const shortText = (max: number) => z.string().trim().max(max);

// ---- Biblioteca de criativos ---------------------------------------------------------------------

export const createAssetSchema = z.object({
  name: shortText(200).min(1, "Informe um nome."),
  kind: z.enum(ASSET_KINDS),
  mimeType: z.string().trim().min(3).max(100),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(4 * 1024 * 1024 * 1024),
  width: z.number().int().positive().max(20000).nullable().default(null),
  height: z.number().int().positive().max(20000).nullable().default(null),
  durationMs: z
    .number()
    .int()
    .nonnegative()
    .max(24 * 3600 * 1000)
    .nullable()
    .default(null),
  /** SHA-256 (hex) do arquivo, calculado no navegador. */
  sha256: z
    .string()
    .trim()
    .regex(/^[0-9a-f]{64}$/, "Hash inválido."),
  folder: shortText(100).default(""),
  tags: z.array(shortText(40).min(1)).max(20).default([]),
  clientId: z.uuid().nullable().default(null),
});
export type CreateAssetInput = z.infer<typeof createAssetSchema>;

export const updateAssetSchema = z.object({
  name: shortText(200).min(1).optional(),
  folder: shortText(100).optional(),
  tags: z.array(shortText(40).min(1)).max(20).optional(),
  clientId: z.uuid().nullable().optional(),
});
export type UpdateAssetInput = z.infer<typeof updateAssetSchema>;

export const assetListQuerySchema = z.object({
  clientId: z.uuid().optional(),
  kind: z.enum(ASSET_KINDS).optional(),
  folder: shortText(100).optional(),
  tag: shortText(40).optional(),
  q: shortText(200).optional(),
});
export type AssetListQuery = z.infer<typeof assetListQuerySchema>;

export const pushAssetSchema = z.object({ adAccountMetaId: actIdSchema });
export type PushAssetInput = z.infer<typeof pushAssetSchema>;

export function isAllowedMime(kind: "imagem" | "video", mime: string): boolean {
  return kind === "imagem"
    ? (IMAGE_MIME_TYPES as readonly string[]).includes(mime)
    : (VIDEO_MIME_TYPES as readonly string[]).includes(mime);
}

// ---- Públicos ---------------------------------------------------------------------------------------

const audienceName = shortText(200).min(2, "Informe um nome com pelo menos 2 caracteres.");

export const websiteRuleSchema = z.object({
  pixelId: metaNumericId,
  /** Dias de retenção (1–180). */
  retentionDays: z.number().int().min(1).max(180).default(30),
  /** Vazio = todos os visitantes do site. */
  urlFilters: z
    .array(
      z.object({
        operator: z.enum(["i_contains", "i_not_contains", "eq"]),
        value: shortText(500).min(1),
      }),
    )
    .max(20)
    .default([]),
});

export const engagementRuleSchema = z.object({
  source: z.enum(ENGAGEMENT_SOURCES),
  /** Página (pagina, video, formulario) ou conta do Instagram (instagram). */
  sourceId: metaNumericId,
  retentionDays: z.number().int().min(1).max(365).default(365),
  /** Vídeos (ids) para `video`; vazio = qualquer vídeo da página. */
  videoIds: z.array(metaNumericId).max(50).default([]),
});

export const createCustomAudienceSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("lista"),
    name: audienceName,
    description: shortText(500).default(""),
  }),
  z.object({
    source: z.literal("site"),
    name: audienceName,
    description: shortText(500).default(""),
    rule: websiteRuleSchema,
  }),
  z.object({
    source: z.literal("engajamento"),
    name: audienceName,
    description: shortText(500).default(""),
    rule: engagementRuleSchema,
  }),
]);
export type CreateCustomAudienceInput = z.infer<typeof createCustomAudienceSchema>;

export const createLookalikeSchema = z.object({
  name: audienceName,
  originAudienceId: metaNumericId,
  country: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/, "Use o código do país (ex.: BR)."),
  /** 1–10% da população do país. */
  percent: z.number().int().min(1).max(10),
});
export type CreateLookalikeInput = z.infer<typeof createLookalikeSchema>;

// ---- Segmentação (compartilhada pelo conjunto e pelos públicos salvos) ------------------------

export const geoItemSchema = z.object({
  key: z.string().trim().min(1).max(100),
  name: shortText(200),
  type: z.enum(["country", "region", "city", "zip"]),
  countryCode: z.string().trim().max(2).default(""),
  /** Raio em km (só cidades; 0 = sem raio). */
  radiusKm: z.number().int().min(0).max(80).default(0),
});
export type GeoItem = z.infer<typeof geoItemSchema>;

export const namedIdSchema = z.object({ id: metaNumericId, name: shortText(200) });

export const targetingSchema = z.object({
  countries: z
    .array(
      z
        .string()
        .trim()
        .regex(/^[A-Z]{2}$/),
    )
    .max(50)
    .default([]),
  regions: z.array(geoItemSchema).max(100).default([]),
  cities: z.array(geoItemSchema).max(200).default([]),
  ageMin: z.number().int().min(13).max(65).default(18),
  ageMax: z.number().int().min(13).max(65).default(65),
  /** [] = todos; [1] homens; [2] mulheres. */
  genders: z
    .array(z.union([z.literal(1), z.literal(2)]))
    .max(2)
    .default([]),
  interests: z.array(namedIdSchema).max(100).default([]),
  customAudiences: z.array(namedIdSchema).max(50).default([]),
  excludedCustomAudiences: z.array(namedIdSchema).max(50).default([]),
  /** Público Advantage+ (a Meta pode ampliar além da segmentação). */
  advantageAudience: z.boolean().default(false),
});
export type TargetingDraft = z.infer<typeof targetingSchema>;

export const placementsSchema = z.object({
  /** Posicionamentos Advantage+ (automáticos). */
  automatic: z.boolean().default(true),
  publisherPlatforms: z.array(z.enum(PUBLISHER_PLATFORMS)).max(4).default([]),
  facebookPositions: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  instagramPositions: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  messengerPositions: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  audienceNetworkPositions: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
});
export type PlacementsDraft = z.infer<typeof placementsSchema>;

export const createSavedAudienceSchema = z.object({
  name: audienceName,
  description: shortText(500).default(""),
  targeting: targetingSchema,
});
export type CreateSavedAudienceInput = z.infer<typeof createSavedAudienceSchema>;

// ---- Assistente: rascunho ------------------------------------------------------------------------

const budgetKindSchema = z.enum(["diario", "vitalicio"]);

export const campaignDraftSchema = z.object({
  name: shortText(400).default(""),
  objective: z.enum(CAMPAIGN_OBJECTIVES).default("OUTCOME_SALES"),
  specialAdCategories: z.array(z.enum(SPECIAL_AD_CATEGORIES)).max(7).default([]),
  budgetMode: z.enum(["CBO", "ABO"]).default("CBO"),
  budgetKind: budgetKindSchema.default("diario"),
  /** Em centavos (só CBO). */
  budgetCents: z.number().int().nonnegative().max(1_000_000_000_000).default(0),
  bidStrategy: z.enum(BID_STRATEGIES).default("LOWEST_COST_WITHOUT_CAP"),
  /** Teste A/B: oculto na interface nesta fase (ver README). */
  abTest: z.boolean().default(false),
});
export type CampaignDraft = z.infer<typeof campaignDraftSchema>;

export const utmSchema = z.object({
  source: shortText(100).default("facebook"),
  medium: shortText(100).default("paid"),
  campaign: shortText(200).default("{{campaign.name}}"),
  content: shortText(200).default("{{ad.name}}"),
  term: shortText(200).default("{{adset.name}}"),
});
export type UtmDraft = z.infer<typeof utmSchema>;

export const carouselCardSchema = z.object({
  assetId: z.uuid().nullable().default(null),
  headline: shortText(255).default(""),
  description: shortText(255).default(""),
  link: shortText(2000).default(""),
});
export type CarouselCardDraft = z.infer<typeof carouselCardSchema>;

export const adDraftSchema = z.object({
  key: z.string().trim().min(1).max(40),
  name: shortText(400).default(""),
  pageId: optionalMetaId,
  instagramUserId: z.string().trim().max(40).nullable().default(null),
  format: z.enum(AD_FORMATS).default("imagem"),
  primaryText: shortText(5000).default(""),
  headline: shortText(255).default(""),
  description: shortText(255).default(""),
  cta: z.enum(CALL_TO_ACTIONS).default("LEARN_MORE"),
  link: shortText(2000).default(""),
  utmEnabled: z.boolean().default(true),
  utm: utmSchema.default({
    source: "facebook",
    medium: "paid",
    campaign: "{{campaign.name}}",
    content: "{{ad.name}}",
    term: "{{adset.name}}",
  }),
  /** Criativo da biblioteca (imagem ou vídeo), por formato. */
  imageAssetId: z.uuid().nullable().default(null),
  videoAssetId: z.uuid().nullable().default(null),
  thumbnailAssetId: z.uuid().nullable().default(null),
  cards: z.array(carouselCardSchema).max(CAROUSEL_MAX_CARDS).default([]),
});
export type AdDraft = z.infer<typeof adDraftSchema>;

export const adsetDraftSchema = z.object({
  key: z.string().trim().min(1).max(40),
  name: shortText(400).default(""),
  destinationType: z.enum(DESTINATION_TYPES).default("WEBSITE"),
  optimizationGoal: z.enum(OPTIMIZATION_GOALS).default("OFFSITE_CONVERSIONS"),
  billingEvent: z.enum(BILLING_EVENTS).default("IMPRESSIONS"),
  pixelId: optionalMetaId,
  customEventType: z.enum(PIXEL_EVENTS).nullable().default(null),
  pageId: optionalMetaId,
  applicationId: z.string().trim().max(40).nullable().default(null),
  objectStoreUrl: shortText(2000).nullable().default(null),
  budgetKind: budgetKindSchema.default("diario"),
  /** Em centavos (só ABO). */
  budgetCents: z.number().int().nonnegative().max(1_000_000_000_000).default(0),
  bidAmountCents: z.number().int().nonnegative().max(1_000_000_000).nullable().default(null),
  startTime: z.string().trim().max(40).nullable().default(null),
  endTime: z.string().trim().max(40).nullable().default(null),
  targeting: targetingSchema.default({
    countries: ["BR"],
    regions: [],
    cities: [],
    ageMin: 18,
    ageMax: 65,
    genders: [],
    interests: [],
    customAudiences: [],
    excludedCustomAudiences: [],
    advantageAudience: false,
  }),
  placements: placementsSchema.default({
    automatic: true,
    publisherPlatforms: [],
    facebookPositions: [],
    instagramPositions: [],
    messengerPositions: [],
    audienceNetworkPositions: [],
  }),
  ads: z.array(adDraftSchema).max(BULK_MAX_ADS).default([]),
});
export type AdSetDraft = z.infer<typeof adsetDraftSchema>;

export const wizardDraftSchema = z.object({
  campaign: campaignDraftSchema.default({
    name: "",
    objective: "OUTCOME_SALES",
    specialAdCategories: [],
    budgetMode: "CBO",
    budgetKind: "diario",
    budgetCents: 0,
    bidStrategy: "LOWEST_COST_WITHOUT_CAP",
    abTest: false,
  }),
  adsets: z.array(adsetDraftSchema).max(BULK_MAX_ADS).default([]),
});
export type WizardDraft = z.infer<typeof wizardDraftSchema>;

export const saveDraftSchema = z.object({
  adAccountMetaId: actIdSchema,
  name: shortText(200).default(""),
  kind: z.enum(DRAFT_KINDS).default("assistente"),
  step: z.enum(WIZARD_STEPS).default("campanha"),
  data: wizardDraftSchema,
  /** Versão lida (controle de concorrência otimista; null = ignorar). */
  version: z.number().int().nonnegative().nullable().default(null),
});
export type SaveDraftInput = z.infer<typeof saveDraftSchema>;

export const publishDraftSchema = z.object({
  /** Ativar ao final (padrão: tudo PAUSED). */
  activate: z.boolean().default(false),
});
export type PublishDraftInput = z.infer<typeof publishDraftSchema>;

export const previewSchema = z.object({
  adAccountMetaId: actIdSchema,
  ad: adDraftSchema,
  format: z
    .enum(PREVIEW_FORMATS.map((f) => f.id) as [string, ...string[]])
    .default("MOBILE_FEED_STANDARD"),
  /** Nomes para resolver os UTMs. */
  campaignName: shortText(400).default(""),
  adsetName: shortText(400).default(""),
});
export type PreviewInput = z.infer<typeof previewSchema>;

export const estimateSchema = z.object({
  adAccountMetaId: actIdSchema,
  optimizationGoal: z.enum(OPTIMIZATION_GOALS),
  targeting: targetingSchema,
  placements: placementsSchema.optional(),
});
export type EstimateInput = z.infer<typeof estimateSchema>;

export const targetingSearchSchema = z.object({
  type: z.enum(["geo", "interesse"]),
  q: shortText(100).min(1),
});
export type TargetingSearchInput = z.infer<typeof targetingSearchSchema>;

// ---- Criação em massa -----------------------------------------------------------------------------

export const bulkMatrixSchema = z.object({
  adAccountMetaId: actIdSchema,
  campaign: campaignDraftSchema,
  /** Modelo de conjunto (orçamento, otimização, agenda, posicionamentos) replicado por público. */
  adsetBase: adsetDraftSchema.omit({ ads: true, key: true, name: true, targeting: true }),
  /** Modelo de anúncio (página, CTA, link, UTMs) replicado por criativo × texto. */
  adBase: adDraftSchema.omit({
    key: true,
    name: true,
    primaryText: true,
    headline: true,
    description: true,
    format: true,
    imageAssetId: true,
    videoAssetId: true,
    cards: true,
  }),
  audiences: z
    .array(z.object({ label: shortText(200).min(1), targeting: targetingSchema }))
    .min(1, "Escolha pelo menos um público.")
    .max(BULK_MAX_ADS),
  creatives: z
    .array(
      z.object({
        label: shortText(200).min(1),
        format: z.enum(["imagem", "video"]),
        assetId: z.uuid(),
      }),
    )
    .min(1, "Escolha pelo menos um criativo.")
    .max(BULK_MAX_ADS),
  texts: z
    .array(
      z.object({
        label: shortText(200).min(1),
        primaryText: shortText(5000),
        headline: shortText(255).default(""),
        description: shortText(255).default(""),
      }),
    )
    .min(1, "Escreva pelo menos um texto.")
    .max(BULK_MAX_ADS),
  adsetNamePattern: shortText(200).default("{campanha} · {publico}"),
  adNamePattern: shortText(200).default("{criativo} · {texto}"),
});
export type BulkMatrixInput = z.infer<typeof bulkMatrixSchema>;

// ---- Templates -------------------------------------------------------------------------------------

export const createTemplateSchema = z
  .object({
    name: shortText(200).min(2, "Informe um nome."),
    description: shortText(1000).default(""),
    /** Origem: um rascunho do workspace ou uma campanha existente da conta. */
    draftId: z.uuid().optional(),
    campaignMetaId: metaNumericId.optional(),
  })
  .refine((v) => v.draftId !== undefined || v.campaignMetaId !== undefined, {
    message: "Informe o rascunho ou a campanha de origem.",
  });
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;

export const useTemplateSchema = z.object({
  adAccountMetaId: actIdSchema,
  name: shortText(200).default(""),
});
export type UseTemplateInput = z.infer<typeof useTemplateSchema>;

// ---- Duplicação entre contas (recriar) -------------------------------------------------------------

export const recreateMappingSchema = z.object({
  pixels: z.record(z.string(), metaNumericId).default({}),
  pages: z.record(z.string(), metaNumericId).default({}),
  instagram: z.record(z.string(), z.string().trim().max(40)).default({}),
  audiences: z.record(z.string(), metaNumericId).default({}),
});
export type RecreateMapping = z.infer<typeof recreateMappingSchema>;

export const recreateSchema = z.object({
  targetAdAccountMetaId: actIdSchema,
  mappings: recreateMappingSchema.default({ pixels: {}, pages: {}, instagram: {}, audiences: {} }),
  /** Publicar logo (ativo ou pausado) ou só salvar como rascunho na conta de destino. */
  mode: z.enum(["rascunho", "publicar"]).default("publicar"),
  activate: z.boolean().default(false),
  /** Sufixo do nome (mantém espaços, ex.: " (Bella)"). */
  renameSuffix: z.string().max(100).default(""),
});
export type RecreateInput = z.infer<typeof recreateSchema>;
